import React, { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Skills.css";
import "../../i18n";
import { useTranslation } from "react-i18next";
import {
  reactIcon,
  javaIcon,
  javascriptIcon,
  htmlIcon,
  cssIcon,
  firebaseIcon,
  cIcon,
  cppIcon,
  pythonIcon,
  figmaIcon,
  sqlIcon,
  apiIcon,
  jiraIcon,
  confluenceIcon,
  gitIcon,
  githubIcon,
  githubBlack,
  bitbucketIcon,
  windowsIcon,
  linuxIcon,
  vscIcon,
  inteliijIcon,
  jsonIcon,
  npmIcon,
  problemIcon,
  problemIconL,
  teamworkIcon,
  teamworkIconL,
  communicationIcon,
  communicationIconL,
  timeIcon,
  timeIconL,
  adaptIcon,
  adaptIconL,
} from "../../assets/";

const Skills = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("tab1");
  const [isLightMode, setIsLightMode] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1080);
  const [sliderHeight, setSliderHeight] = useState("auto");
  const sliderRef = useRef(null);
  const slideContainerRef = useRef(null);

  const updateTheme = () => {
    const currentTheme = document.documentElement.getAttribute("data-theme");
    setIsLightMode(currentTheme === "light");
  };

  useEffect(() => {
    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1080);
      updateSliderHeight();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const updateSliderHeight = () => {
    if (slideContainerRef.current) {
      const activeSlide =
        slideContainerRef.current.querySelector(".slick-active");
      if (activeSlide) {
        setSliderHeight(`${activeSlide.scrollHeight}px`);
      }
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateSliderHeight();
    });

    if (slideContainerRef.current) {
      resizeObserver.observe(slideContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    afterChange: updateSliderHeight,
  };

  return (
    <div className="skills" id="skills">
      <div className="wrapper">
        <div className="name">
          <p>{t("skills")}</p>
        </div>
        <div className="main-skills">
          {!isMobile ? (
            <div className="tab-navigation">
              <button
                className={activeTab === "tab1" ? "active" : ""}
                onClick={() => setActiveTab("tab1")}
              >
                {t("section1")}
              </button>
              <button
                className={activeTab === "tab2" ? "active" : ""}
                onClick={() => setActiveTab("tab2")}
              >
                {t("section2")}
              </button>
              <button
                className={activeTab === "tab3" ? "active" : ""}
                onClick={() => setActiveTab("tab3")}
              >
                {t("section3")}
              </button>
              <button
                className={activeTab === "tab4" ? "active" : ""}
                onClick={() => setActiveTab("tab4")}
              >
                {t("section4")}
              </button>
              <button
                className={activeTab === "tab5" ? "active" : ""}
                onClick={() => setActiveTab("tab5")}
              >
                {t("section5")}
              </button>
            </div>
          ) : (
            <div
              className="slider-container"
              ref={slideContainerRef}
              style={{ height: sliderHeight }}
            >
              <Slider {...sliderSettings} className="skills-slider">
                <div>
                  <SkillsTab1 t={t} isLightMode={isLightMode} />
                </div>
                <div>
                  <SkillsTab2 t={t} isLightMode={isLightMode} />
                </div>
                <div>
                  <SkillsTab3 t={t} isLightMode={isLightMode} />
                </div>
                <div>
                  <SkillsTab4 t={t} isLightMode={isLightMode} />
                </div>
                <div>
                  <SkillsTab5 t={t} isLightMode={isLightMode} />
                </div>
              </Slider>
            </div>
          )}
          {!isMobile && (
            <div className="content">
              {activeTab === "tab1" && (
                <SkillsTab1 t={t} isLightMode={isLightMode} />
              )}
              {activeTab === "tab2" && (
                <SkillsTab2 t={t} isLightMode={isLightMode} />
              )}
              {activeTab === "tab3" && (
                <SkillsTab3 t={t} isLightMode={isLightMode} />
              )}
              {activeTab === "tab4" && (
                <SkillsTab4 t={t} isLightMode={isLightMode} />
              )}
              {activeTab === "tab5" && (
                <SkillsTab5 t={t} isLightMode={isLightMode} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SkillsTab1 = ({ t, isLightMode }) => (
  <div className="skills-container">
    <h2 className="skills-header">{t("section1")}</h2>
    <div className="skills-list">
      <div className="skill-element">
        <div className="skill-name">React</div>
        <div className="skill-icon">
          <img src={reactIcon} alt="React Icon" className="reactIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">React Native</div>
        <div className="skill-icon">
          <img src={reactIcon} alt="React Native Icon" className="reactIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">JavaScript</div>
        <div className="skill-icon">
          <img
            src={javascriptIcon}
            alt="JavaScript Icon"
            className="javascriptIcon"
          />
        </div>
        <div className="skill-level">{t("begginer")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">HTML</div>
        <div className="skill-icon">
          <img src={htmlIcon} alt="HTML Icon" className="htmlIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">CSS</div>
        <div className="skill-icon">
          <img src={cssIcon} alt="CSS Icon" className="cssIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">JSON</div>
        <div className="skill-icon">
          <img src={jsonIcon} alt="JSON Icon" className="jsonIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">NPM</div>
        <div className="skill-icon">
          <img src={npmIcon} alt="NPM Icon" className="npmIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>
    </div>
  </div>
);

const SkillsTab2 = ({ t, isLightMode }) => (
  <div className="skills-container">
    <h2 className="skills-header">{t("section2")}</h2>
    <div className="skills-list">
      <div className="skill-element">
        <div className="skill-name">Firebase</div>
        <div className="skill-icon">
          <img
            src={firebaseIcon}
            alt="Firebase Icon"
            className="firebaseIcon"
          />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">API Rest</div>
        <div className="skill-icon">
          <img src={apiIcon} alt="API Icon" className="apiIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">SQL</div>
        <div className="skill-icon">
          <img src={sqlIcon} alt="SQL Icon" className="sqlIcon" />
        </div>
        <div className="skill-level">{t("begginer")}</div>
      </div>
    </div>
  </div>
);

const SkillsTab3 = ({ t, isLightMode }) => (
  <div className="skills-container">
    <h2 className="skills-header">{t("section3")}</h2>
    <div className="skills-list">
      <div className="skill-element">
        <div className="skill-name">Java</div>
        <div className="skill-icon">
          <img src={javaIcon} alt="Java Icon" className="javaIcon" />
        </div>
        <div className="skill-level">{t("begginer")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">C</div>
        <div className="skill-icon">
          <img src={cIcon} alt="C Icon" className="cIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">C++</div>
        <div className="skill-icon">
          <img src={cppIcon} alt="C++ Icon" className="cppIcon" />
        </div>
        <div className="skill-level">{t("begginer")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">Python</div>
        <div className="skill-icon">
          <img src={pythonIcon} alt="Python Icon" className="pythonIcon" />
        </div>
        <div className="skill-level">{t("competent")}</div>
      </div>
    </div>
  </div>
);

const SkillsTab4 = ({ t, isLightMode }) => (
  <div className="skills-container">
    <h2 className="skills-header">{t("section4")}</h2>
    <div className="skills-list">
      <div className="skill-element">
        <div className="skill-name">Figma</div>
        <div className="skill-icon">
          <img src={figmaIcon} alt="Figma Icon" className="figmaIcon" />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">GIT</div>
        <div className="skill-icon">
          <img src={gitIcon} alt="GIT Icon" className="gitIcon" />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">GitHub</div>
        <div className="skill-icon">
          <img
            src={isLightMode ? githubBlack : githubIcon}
            alt="GitHub Icon"
            className="githubIcon"
          />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">Bitbucket</div>
        <div className="skill-icon">
          <img
            src={bitbucketIcon}
            alt="Bitbucket Icon"
            className="bitbucketIcon"
          />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">Jira</div>
        <div className="skill-icon">
          <img src={jiraIcon} alt="Jira Icon" className="jiraIcon" />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">Confluence</div>
        <div className="skill-icon">
          <img
            src={confluenceIcon}
            alt="Confluence Icon"
            className="confluenceIcon"
          />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">Windows</div>
        <div className="skill-icon">
          <img src={windowsIcon} alt="Windows Icon" className="windowsIcon" />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">Linux</div>
        <div className="skill-icon">
          <img src={linuxIcon} alt="Linux Icon" className="linuxIcon" />
        </div>
        <div className="skill-level">{t("begginer")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">VS Code</div>
        <div className="skill-icon">
          <img src={vscIcon} alt="VS Code Icon" className="vscIcon" />
        </div>
        <div className="skill-level">{t("proficient")}</div>
      </div>

      <div className="skill-element">
        <div className="skill-name">IntelliJ</div>
        <div className="skill-icon">
          <img
            src={inteliijIcon}
            alt="IntelliJ Icon"
            className="inteliijIcon"
          />
        </div>
        <div className="skill-level">{t("begginer")}</div>
      </div>
    </div>
  </div>
);

const SkillsTab5 = ({ t, isLightMode }) => (
  <div className="skills-container">
    <h2 className="skills-header">{t("section5")}</h2>
    <div className="skills-list">
      <div className="skill-element">
        <div className="skill-name">{t("problemSolving")}</div>
        <div className="skill-icon">
          <img
            src={isLightMode ? problemIconL : problemIcon}
            alt="Problem Solving Icon"
            className="problemIcon"
          />
        </div>
      </div>

      <div className="skill-element">
        <div className="skill-name">{t("teamwork")}</div>
        <div className="skill-icon">
          <img
            src={isLightMode ? teamworkIconL : teamworkIcon}
            alt="Teamwork Icon"
            className="teamworkIcon"
          />
        </div>
      </div>

      <div className="skill-element">
        <div className="skill-name">{t("communication")}</div>
        <div className="skill-icon">
          <img
            src={isLightMode ? communicationIconL : communicationIcon}
            alt="Communication Icon"
            className="communicationIcon"
          />
        </div>
      </div>

      <div className="skill-element">
        <div className="skill-name">{t("timeManagement")}</div>
        <div className="skill-icon">
          <img
            src={isLightMode ? timeIconL : timeIcon}
            alt="Time Management Icon"
            className="timeIcon"
          />
        </div>
      </div>

      <div className="skill-element">
        <div className="skill-name">{t("adaptability")}</div>
        <div className="skill-icon">
          <img
            src={isLightMode ? adaptIconL : adaptIcon}
            alt="Adaptability Icon"
            className="adaptIcon"
          />
        </div>
      </div>
    </div>
  </div>
);

export default Skills;
